import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import { tertiaryText, smallTextSize, primaryText } from "../../styling/stylingHelper";

const POPOVER_WIDTH = 260;
const POPOVER_MARGIN = 8;

interface RestaurantNamesCellProps {
    restaurantNames?: string[];
}

/**
 * "All restaurants" table cell: shows how many restaurants ordered a product and
 * reveals their names on CLICK (a hover tooltip was too hard to discover / read).
 */
const RestaurantNamesCell: React.FC<RestaurantNamesCellProps> = ({ restaurantNames }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    const names = Array.isArray(restaurantNames) ? restaurantNames : [];

    const calculatePosition = useCallback(() => {
        if (!triggerRef.current) return;

        const rect = triggerRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        let left = rect.left + rect.width / 2 - POPOVER_WIDTH / 2;
        left = Math.max(POPOVER_MARGIN, Math.min(left, viewportWidth - POPOVER_WIDTH - POPOVER_MARGIN));

        setPosition({ top: rect.bottom + POPOVER_MARGIN, left });
    }, []);

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isOpen) {
            setIsOpen(false);
        } else {
            calculatePosition();
            setIsOpen(true);
        }
    };

    // Close on click outside / scroll / escape
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (e: MouseEvent) => {
            if (
                popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
                triggerRef.current && !triggerRef.current.contains(e.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        const handleScroll = () => setIsOpen(false);
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") setIsOpen(false);
        };

        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("scroll", handleScroll, true);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("scroll", handleScroll, true);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen]);

    // Flip above the trigger when there is not enough room below
    useEffect(() => {
        if (!isOpen || !popoverRef.current || !triggerRef.current) return;

        const rect = triggerRef.current.getBoundingClientRect();
        const popoverHeight = popoverRef.current.offsetHeight;
        const spaceBelow = window.innerHeight - rect.bottom;
        if (spaceBelow < popoverHeight + POPOVER_MARGIN) {
            setPosition(prev => ({ ...prev, top: Math.max(POPOVER_MARGIN, rect.top - popoverHeight - POPOVER_MARGIN) }));
        }
    }, [isOpen]);

    if (names.length === 0) {
        return <span className={cn(tertiaryText, smallTextSize)}>--</span>;
    }

    if (names.length === 1) {
        return <span className={cn(tertiaryText, smallTextSize)}>{names[0]}</span>;
    }

    return (
        <>
            <button
                ref={triggerRef}
                onClick={handleToggle}
                aria-expanded={isOpen}
                className={cn(
                    "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[13px] font-medium transition-colors",
                    "bg-borderLightGrey dark:bg-borderLightGrey-dark text-textSecondary dark:text-textSecondary-dark",
                    "hover:bg-borderGrey dark:hover:bg-borderPrimary-dark cursor-pointer"
                )}
            >
                {names.length} {t('orderHistory.restaurantsShort')}
                <ChevronDown size={12} className={cn("transition-transform", isOpen && "rotate-180")} />
            </button>

            {isOpen && createPortal(
                <div
                    ref={popoverRef}
                    style={{
                        position: "fixed",
                        top: position.top,
                        left: position.left,
                        width: POPOVER_WIDTH,
                        zIndex: 9999,
                    }}
                    className="bg-white dark:bg-card-dark border border-borderGrey dark:border-borderPrimary-dark rounded-xl shadow-lg"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-center justify-between px-4 pt-3 pb-2">
                        <span className="text-[13px] font-semibold text-textPrimary dark:text-textPrimary-dark">
                            {t('orderHistory.restaurantsThatOrdered')}
                        </span>
                        <button
                            onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
                            className="text-textQuaternary dark:text-textTertiary-dark hover:text-textSecondary dark:hover:text-textSecondary-dark transition-colors"
                            aria-label={t('common.close')}
                        >
                            <X size={14} />
                        </button>
                    </div>
                    <ul className="px-4 pb-3 max-h-[220px] overflow-y-auto flex flex-col gap-1">
                        {names.map((name, index) => (
                            <li key={`${name}-${index}`} className={cn(primaryText, "text-[12px] leading-[18px]")}>
                                {name}
                            </li>
                        ))}
                    </ul>
                </div>,
                document.body
            )}
        </>
    );
};

export default RestaurantNamesCell;
